import { HttpInterceptorFn, HttpRequest, HttpHandlerFn, HttpErrorResponse, HttpClient } from '@angular/common/http';
import { inject } from '@angular/core';
import { BehaviorSubject, Observable, from, throwError } from 'rxjs';
import { catchError, filter, take, switchMap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { AuthService } from '../services/auth.service';
import { currentPharmacyAnf, setCurrentPharmacyAnf, PHARMACY_HEADER, RELOAD_PAGE } from '../pharmacy-context';
import { SsoService } from '../services/sso.service';

// Global state for token refresh
let isRefreshing = false;
let refreshFailed = false;
const refreshTokenSubject = new BehaviorSubject<string | null>(null);

/** A stored ANF the person no longer holds (another person on this browser, a pharmacy lost) must not lock the tab out:
 *  drop it and reload the page, which starts again at the API's default pharmacy. Nothing is replayed, so a write never
 *  lands on another pharmacy (I3). The ANF is the one this request carried, read before it went out. */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const sent = currentPharmacyAnf();
  const reloadPage = inject(RELOAD_PAGE);
  return authInterceptorCore(req, next).pipe(
    catchError((error: HttpErrorResponse) => {
      if (sent && error.status === 403 && error.error?.reason === 'pharmacy_not_in_scope') {
        setCurrentPharmacyAnf(null);
        reloadPage();
      }
      return throwError(() => error);
    })
  );
};

const authInterceptorCore: HttpInterceptorFn = (originalReq: HttpRequest<any>, next: HttpHandlerFn): Observable<any> => {
  let req = originalReq;
  // Inject services at interceptor level (injection context)
  const http = inject(HttpClient);
  const authService = inject(AuthService);

  // Skip auth endpoints that don't need tokens
  if (isAuthEndpoint(req.url)) {
    return next(req);
  }

  const anf = currentPharmacyAnf();
  if (anf) req = req.clone({ setHeaders: { [PHARMACY_HEADER]: anf } });

  // login-farmacias session: MSAL owns the token and refreshes it; the Supabase refresh flow below must
  // never run for it. A 401 that survives a fresh token means re-authentication, not a refresh call.
  if (SsoService.isSsoSession()) {
    const sso = inject(SsoService);
    return from(sso.requestHeaders()).pipe(
      switchMap((headers) => next(req.clone({ setHeaders: headers, body: req.body }))),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401 && !req.url.includes('/auth/')) {
          sso.reauthenticate();
        }
        return throwError(() => error);
      })
    );
  }

  // Check if token is expired BEFORE making the request (proactive refresh)
  const token = getAccessToken();
  const refreshToken = getRefreshToken();
  
  // If token is expired and we have a refresh token, refresh proactively
  if (token && refreshToken && authService.isTokenExpired() && !isRefreshing) {
    console.log('[Token Refresh] Token expired, refreshing proactively before request:', req.url);
    return handleTokenRefresh(req, next, http, authService);
  }

  // If refresh is already in progress, wait for it
  if (isRefreshing) {
    return handleConcurrentRequest(req, next, authService);
  }

  // Add token to request
  if (token) {
    req = addTokenToRequest(req, token);
  }

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // Fallback: if we still get 401 (token might have expired between check and request)
      if (error.status === 401 && !isRefreshing) {
        return handleTokenRefresh(req, next, http, authService);
      }

      if (error.status === 401 && isRefreshing) {
        return handleConcurrentRequest(req, next, authService);
      }

      return throwError(() => error);
    })
  );
};

function isAuthEndpoint(url: string): boolean {
  return url.includes('/auth/signin') ||
    url.includes('/auth/signout') ||
    url.includes('/auth/signup') ||
    url.includes('/auth/refresh') ||
    url.includes('/auth/validate-and-refresh') ||
    url.includes('/auth/reset-password') ||
    url.includes('/auth/complete-invitation');
}

function getAccessToken(): string | null {
  return localStorage.getItem('ezfy_access_token');
}

function getRefreshToken(): string | null {
  return localStorage.getItem('ezfy_refresh_token');
}

function addTokenToRequest(request: HttpRequest<any>, token: string): HttpRequest<any> {
  // Explicitly preserve the body when cloning to ensure FormData is not lost
  // This is critical when using withFetch() as it handles FormData differently
  return request.clone({
    setHeaders: {
      Authorization: `Bearer ${token}`
    },
    body: request.body // Explicitly preserve body to ensure FormData is maintained
  });
}

function handleTokenRefresh(req: HttpRequest<any>, next: HttpHandlerFn, http: HttpClient, authService: AuthService): Observable<any> {
  const refreshToken = getRefreshToken();

  // Don't redirect to login for auth-related endpoints (they handle their own errors)
  const isAuthRelatedEndpoint = req.url.includes('/auth/');

  if (!refreshToken) {
    // No refresh token available
    // Only redirect to login if this is not an auth-related endpoint
    // Auth endpoints should handle errors themselves without redirecting
    if (!isAuthRelatedEndpoint) {
      redirectToLogin();
    }
    return throwError(() => new Error('No refresh token available'));
  }

  isRefreshing = true;
  refreshFailed = false;
  refreshTokenSubject.next(null);
  authService.setRefreshing(true); // Sync with AuthService

  const refreshRequest = { refreshToken };

  console.log('[Token Refresh] Starting refresh request to API...', {
    url: `${environment.apiUrl}/auth/refresh`,
    hasRefreshToken: !!refreshToken
  });

  return http.post<any>(`${environment.apiUrl}/auth/refresh`, refreshRequest).pipe(
    switchMap((session) => {
      isRefreshing = false;
      refreshFailed = false;
      authService.setRefreshing(false); // Sync with AuthService

      console.log('[Token Refresh] ✅ Refresh successful!', {
        newAccessTokenLength: session.accessToken?.length,
        newRefreshTokenLength: session.refreshToken?.length,
        expiresAt: session.expiresAt,
        originalRequest: req.url
      });

      // New tokens; the user in hand stays and is reloaded from /auth/me (the refresh payload has no pharmacies).
      authService.applyRefreshedSession(session);

      refreshTokenSubject.next(session.accessToken);
      return next(addTokenToRequest(req, session.accessToken));
    }),
    catchError((refreshError) => {
      isRefreshing = false;
      refreshFailed = true;
      authService.setRefreshing(false); // Sync with AuthService
      
      const errorStatus = refreshError?.status;
      const errorMessage = refreshError?.error?.message || refreshError?.message || 'Unknown error';
      
      console.error('[Token Refresh] ❌ Refresh failed!', {
        error: refreshError,
        status: errorStatus,
        message: errorMessage,
        errorResponse: refreshError?.error,
        originalRequest: req.url,
        refreshTokenUsed: refreshToken?.substring(0, 10) + '...'
      });
      
      // Only log out if it's an authentication error (401, 403) or refresh token invalid
      // Network errors or server errors (500) should not log out the user
      const isAuthError = errorStatus === 401 || errorStatus === 403;
      const isRefreshTokenInvalid = errorMessage?.toLowerCase().includes('refresh') || 
                                    errorMessage?.toLowerCase().includes('token') ||
                                    errorMessage?.toLowerCase().includes('invalid');
      
      if (isAuthError || isRefreshTokenInvalid) {
        console.log('[Token Refresh] Authentication error detected, logging out user');
        
        // Attempt to sign out on server (fire and forget - don't block)
        // This invalidates refresh tokens server-side for security
        // If it fails (token already invalid), that's fine - we'll clear local state anyway
        authService.signOut().subscribe({
          error: () => {
            // Sign-out failed silently (token might already be invalid) - that's expected
          }
        });
        
        // Clear local state immediately (don't wait for sign-out API call)
        authService.clearAuth();
        
        // Signal waiting concurrent requests that refresh failed
        refreshTokenSubject.next(null);
        
        // Don't redirect to login for auth-related endpoints (they handle their own errors)
        const isAuthRelatedEndpoint = req.url.includes('/auth/');
        if (!isAuthRelatedEndpoint) {
          redirectToLogin();
        }
      } else {
        // Network or server error - don't log out, just fail the request
        console.warn('[Token Refresh] Non-auth error during refresh, not logging out:', errorStatus);
        refreshTokenSubject.next(null);
      }
      
      return throwError(() => refreshError);
    })
  );
}

function handleConcurrentRequest(req: HttpRequest<any>, next: HttpHandlerFn, authService: AuthService): Observable<any> {
  return refreshTokenSubject.pipe(
    filter(token => token !== null || refreshFailed),
    take(1),
    switchMap((token) => {
      if (refreshFailed || token === null) {
        // Refresh failed, clear auth and throw error
        authService.clearAuth();
        const isAuthRelatedEndpoint = req.url.includes('/auth/');
        if (!isAuthRelatedEndpoint) {
          redirectToLogin();
        }
        return throwError(() => new Error('Token refresh failed'));
      }
      return next(addTokenToRequest(req, token));
    })
  );
}

function redirectToLogin(): void {
  // Clear all auth data
  localStorage.removeItem('ezfy_access_token');
  localStorage.removeItem('ezfy_refresh_token');
  localStorage.removeItem('ezfy_expires_at');
  localStorage.removeItem('ezfy_user');
  setCurrentPharmacyAnf(null);

  // Redirect to login page
  window.location.href = '/login';
} 